import React, { useContext, useEffect, useState } from "react";
import { UserContext } from "@/context/user";
import { ConsultationContext } from "@/context/consultation";

export const ConsultationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [meetingLink, setMeetingLink] = useState<string | null>(null);
  const [meetingTime, setMeetingTime] = useState<string | null>(null);
  const [isBannerHidden, setIsBannerHidden] = useState<boolean>(
    localStorage.getItem("hideConsultationBanner") === "true" || false,
  );

  useEffect(() => {
    const checkStatus = async () => {
      if (!user) {
        setShowBanner(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/consultation/status`,
          {
            method: "GET",
            credentials: "include",
          },
        );

        if (response.ok) {
          const data = (await response.json()) as {
            hasScheduledCall: boolean;
            meetingLink: string | null;
            meetingTime: string | null;
          };
          setMeetingLink(data.meetingLink);
          setMeetingTime(data.meetingTime);

          if (data.hasScheduledCall && data.meetingTime) {
            const meetingDate = new Date(data.meetingTime);
            const now = new Date();
            setShowBanner(meetingDate > now);
          } else {
            setShowBanner(true);
          }
        } else {
          setShowBanner(false);
        }
      } catch (error) {
        setShowBanner(false);
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [user]);

  useEffect(() => {
    localStorage.setItem("hideConsultationBanner", String(isBannerHidden));
  }, [isBannerHidden]);

  const handleScheduleCall = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/consultation/schedule`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      if (response.ok) {
        const { url } = await response.json();
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error scheduling consultation:", error);
    }
  };

  const handleCloseBanner = () => {
    setIsBannerHidden(true);
  };

  const contextValue = {
    showBanner: showBanner && !isLoading && !isBannerHidden,
    meetingLink,
    meetingTime,
    handleScheduleCall,
    handleCloseBanner,
  };

  return (
    <ConsultationContext.Provider value={contextValue}>
      {children}
    </ConsultationContext.Provider>
  );
};
